import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';
import { getSpecialById, getArticlesByIds, getAllSpecials } from '@/utils/data';
import { formatViews } from '@/utils/format';

export default function SpecialPage() {
  const { id } = useParams<{ id: string }>();
  const special = id ? getSpecialById(id) : undefined;

  useSeo({
    title: special ? `${special.title} - 专题 - 51吃瓜网` : '专题 - 51吃瓜网',
    description: special ? special.intro : '51吃瓜网专题合集',
    keywords: special ? `${special.title},${special.tag},51吃瓜网专题` : '51吃瓜网,专题',
  });

  if (!special) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-bookmark-line text-3xl" />
          </div>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-900">这个专题不存在或已下线</h1>
          <p className="mt-2 text-sm text-foreground-500">看看其他专题合集吧。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const list = getArticlesByIds(special.articleIds);
  const others = getAllSpecials().filter((s) => s.id !== special.id).sort((a, b) => b.views - a.views).slice(0, 3);

  return (
    <SiteLayout>
      <div className="w-full">
        <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden bg-foreground-950">
          <img src={special.cover} alt={special.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10" />
          <div className="absolute inset-0 w-full flex items-end">
            <div className="w-full px-4 md:px-8 pb-10">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-3">
                <i className="ri-bookmark-3-line" /> {special.tag}专题
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white max-w-3xl leading-snug">{special.title}</h1>
              <p className="hidden md:block mt-3 text-base text-white/85 max-w-3xl">{special.intro}</p>
              <div className="flex items-center gap-4 mt-4 text-sm text-white/75">
                <span className="flex items-center gap-1"><i className="ri-eye-line" /> {formatViews(special.views)} 阅读</span>
                <span className="flex items-center gap-1"><i className="ri-stack-line" /> {list.length} 篇内容</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: '专题', to: '/special/s1' }, { label: special.title }]} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-list-check text-sm" />
                </span>
                专题内容
              </h2>

              <div className="space-y-4">
                {list.map((article, i) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                  >
                    <div className="relative w-full sm:w-48 h-40 sm:h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">第 {i + 1} 篇</span>
                        <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs">{article.subCategoryName}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="hidden sm:block mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.summary}</p>
                      <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1"><i className="ri-time-line" /> {article.source}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line" /> {formatViews(article.views)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1 space-y-6">
              <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
                <h2 className="font-heading text-base font-bold text-accent-900 flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                    <i className="ri-lightbulb-line text-sm" />
                  </span>
                  推荐理由
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{special.reason}</p>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-bookmark-3-line text-sm" />
                  </span>
                  相关专题
                </h2>
                <div className="space-y-2">
                  {others.map((sp) => (
                    <Link
                      key={sp.id}
                      to={`/special/${sp.id}`}
                      className="block px-3 py-2.5 rounded-md bg-background-100 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                    >
                      {sp.title}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}